import { useState, useEffect, useCallback } from "react";
import { authFetch } from "../api";
import Header from "../components/Header";
import NeighborhoodSelect from "../components/NeighborhoodSelect";
import ListingEditModal from "../components/ListingEditModal";

const RENO_OPTIONS = [
  { value: "", label: "-" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
];

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "2-digit",
    });
  } catch {
    return iso.slice(0, 10);
  }
};

const formatPrice = (p) => {
  if (!p) return "-";
  return `$${Math.round(p).toLocaleString()}`;
};

const STATUS_COLORS = {
  ACTIVE: "#4caf50",
  "BACK ON MKT": "#4caf50",
  RENTED: "#1976d2",
  "RENTED THRU US": "#1976d2",
  "LSE SIGN": "#1976d2",
  CLOSED: "#9e9e9e",
  "PERM OFF MKT": "#9e9e9e",
  "TEMP OFF MKT": "#ff9800",
  "CONTRACT SIGNED": "#ff9800",
};

export default function UnitsPage() {
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [missingFields, setMissingFields] = useState(false);
  const [units, setUnits] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedUnit, setExpandedUnit] = useState(null);
  const [unitHistory, setUnitHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [editListing, setEditListing] = useState(null);
  const LIMIT = 50;

  useEffect(() => {
    authFetch("/sale-reviews/neighborhoods")
      .then((data) => setNeighborhoods(data.neighborhoods || []))
      .catch(console.error);
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedNeighborhood) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        neighborhood_id: selectedNeighborhood,
        page,
        limit: LIMIT,
      });
      if (search) params.append("search", search);
      if (missingFields) params.append("missing_fields", "true");
      const data = await authFetch(`/listings/units?${params}`);
      setUnits(data.units || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Failed to load units:", err);
    }
    setLoading(false);
  }, [selectedNeighborhood, page, search, missingFields]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadHistory = async (unitId) => {
    if (expandedUnit === unitId) {
      setExpandedUnit(null);
      setUnitHistory(null);
      return;
    }
    setExpandedUnit(unitId);
    setHistoryLoading(true);
    try {
      const data = await authFetch(`/listings/units/${unitId}/history`);
      setUnitHistory(data);
    } catch (err) {
      console.error("Failed to load unit history:", err);
    }
    setHistoryLoading(false);
  };

  const saveField = async (unitId, field, value) => {
    setSaving(unitId);
    try {
      await authFetch(`/listings/units/${unitId}`, {
        method: "PATCH",
        body: JSON.stringify({ [field]: value }),
      });
      // Refresh the row
      setUnits((prev) =>
        prev.map((u) => (u.unit_id === unitId ? { ...u, [field]: value } : u))
      );
    } catch (err) {
      console.error("Save failed:", err);
    }
    setSaving(null);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const totalPages = Math.ceil(total / LIMIT);

  // Count filled fields for a unit
  const filledCount = (u) => {
    let c = 0;
    if (u.dishwasher !== null) c++;
    if (u.washer_dryer) c++;
    if (u.outdoor_type || u.private_outdoor_space !== null) c++;
    if (u.total_sqft) c++;
    if (u.ai_renovation) c++;
    if (u.ai_floor !== null) c++;
    return c;
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <Header showLogout />
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "24px 16px" }}>
        <h2 style={{ margin: "0 0 16px" }}>Units</h2>

        {/* Filters */}
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-end",
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          <NeighborhoodSelect
            neighborhoods={neighborhoods}
            selected={selectedNeighborhood}
            onChange={(id) => {
              setSelectedNeighborhood(id);
              setPage(1);
            }}
            multi={false}
            label="Neighborhood"
          />

          <form onSubmit={handleSearch} style={{ display: "flex", gap: "8px" }}>
            <input
              type="text"
              placeholder="Search by address, listing ID... (use # for unit)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{
                padding: "10px 16px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
                width: "350px",
              }}
            />
            <button
              type="submit"
              style={{
                padding: "10px 20px",
                backgroundColor: "#000",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Search
            </button>
            {search && (
              <button
                type="button"
                onClick={() => { setSearchInput(""); setSearch(""); setPage(1); }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#f5f5f5",
                  color: "#333",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            )}
          </form>

          <label
            style={{
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 4,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={missingFields}
              onChange={(e) => {
                setMissingFields(e.target.checked);
                setPage(1);
              }}
            />
            Missing data only
          </label>

          <span style={{ fontSize: 13, color: "#666", marginLeft: "auto" }}>
            {total.toLocaleString()} units
          </span>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#999" }}>
            Loading...
          </div>
        ) : !selectedNeighborhood ? (
          <div style={{ padding: 40, textAlign: "center", color: "#999" }}>
            Select a neighborhood
          </div>
        ) : (
          <>
            <div
              style={{
                background: "#fff",
                borderRadius: 8,
                overflow: "auto",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#f9f9f9",
                      borderBottom: "2px solid #eee",
                    }}
                  >
                    <th style={th}></th>
                    <th style={th}>Address</th>
                    <th style={th}>Unit</th>
                    <th style={th}>Beds</th>
                    <th style={th}>Baths</th>
                    <th style={th}>Sqft</th>
                    <th style={th}>DW</th>
                    <th style={th}>W/D</th>
                    <th style={th}>Outdoor</th>
                    <th style={th}>Reno</th>
                    <th style={th}>Floor</th>
                    <th style={th}>Views</th>
                    <th style={th}>Verified</th>
                    <th style={th}>Fields</th>
                    <th style={th}># Listings</th>
                    <th style={th}>Latest</th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((u) => (
                    <>
                      <tr
                        key={u.unit_id}
                        style={{
                          borderBottom: "1px solid #f0f0f0",
                          cursor: "pointer",
                          background:
                            expandedUnit === u.unit_id ? "#f0f7ff" : "#fff",
                        }}
                        onClick={() => loadHistory(u.unit_id)}
                      >
                        <td style={td}>
                          <span
                            style={{
                              fontSize: 10,
                              color: "#999",
                              transition: "transform 0.2s",
                              display: "inline-block",
                              transform:
                                expandedUnit === u.unit_id
                                  ? "rotate(90deg)"
                                  : "rotate(0)",
                            }}
                          >
                            &#9654;
                          </span>
                        </td>
                        <td style={td}>
                          <a
                            href={`/buildings/${u.property_id}/edit`}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            style={{ fontWeight: 500, color: "#333", textDecoration: "none" }}
                            title="Open building"
                          >
                            {u.address}
                          </a>
                        </td>
                        <td style={td}>{u.unit_number}</td>
                        <td style={td}>{u.bedrooms ?? "-"}</td>
                        <td style={td}>{u.bathrooms ?? "-"}</td>
                        <td style={td} onClick={(e) => e.stopPropagation()}>
                          <EditableNumber
                            value={u.total_sqft}
                            placeholder={
                              u.estimated_sqft
                                ? `~${u.estimated_sqft}`
                                : "-"
                            }
                            onSave={(v) =>
                              saveField(u.unit_id, "total_sqft", v)
                            }
                          />
                        </td>
                        <td style={td} onClick={(e) => e.stopPropagation()}>
                          <EditableCheckbox
                            value={u.dishwasher}
                            onSave={(v) =>
                              saveField(u.unit_id, "dishwasher", v)
                            }
                          />
                        </td>
                        <td style={td} onClick={(e) => e.stopPropagation()}>
                          <EditableSelect
                            value={u.washer_dryer}
                            options={[
                              { value: "", label: "-" },
                              { value: "Y", label: "Y" },
                              { value: "N", label: "N" },
                            ]}
                            onSave={(v) =>
                              saveField(u.unit_id, "washer_dryer", v)
                            }
                          />
                        </td>
                        <td style={td} onClick={(e) => e.stopPropagation()}>
                          <EditableSelect
                            value={u.outdoor_type || (u.private_outdoor_space ? "unknown" : "")}
                            options={[
                              { value: "", label: "No" },
                              { value: "juliet", label: "Juliet" },
                              { value: "balcony", label: "Balcony" },
                              { value: "terrace", label: "Terrace" },
                              { value: "garden", label: "Garden" },
                              { value: "roof_deck", label: "Roof Dk" },
                              { value: "unknown", label: "Unk" },
                            ]}
                            onSave={(v) =>
                              saveField(u.unit_id, "outdoor_type", v || "none")
                            }
                          />
                        </td>
                        <td style={td} onClick={(e) => e.stopPropagation()}>
                          <EditableSelect
                            value={u.ai_renovation}
                            options={RENO_OPTIONS}
                            onSave={(v) =>
                              saveField(u.unit_id, "ai_renovation", v)
                            }
                          />
                        </td>
                        <td style={td} onClick={(e) => e.stopPropagation()}>
                          <EditableNumber
                            value={u.ai_floor}
                            placeholder="-"
                            onSave={(v) =>
                              saveField(u.unit_id, "ai_floor", v)
                            }
                            width={40}
                          />
                        </td>
                        <td style={td} onClick={(e) => e.stopPropagation()}>
                          <EditableSelect
                            value={u.ai_views}
                            options={[
                              { value: "", label: "-" },
                              { value: "0", label: "No" },
                              { value: "1", label: "Yes" },
                            ]}
                            onSave={(v) =>
                              saveField(u.unit_id, "ai_views", v)
                            }
                          />
                        </td>
                        <td style={td}>
                          {u.verified_at ? (
                            <span
                              style={{ color: "#4caf50", fontWeight: 600 }}
                              title={u.verified_at}
                            >
                              V
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td style={td}>
                          <span
                            style={{
                              color: filledCount(u) === 6 ? "#4caf50" : "#666",
                              fontWeight:
                                filledCount(u) === 6 ? 600 : 400,
                            }}
                          >
                            {filledCount(u)}/6
                          </span>
                        </td>
                        <td style={td}>{u.listing_count}</td>
                        <td style={td}>
                          {u.latest_listing ? (
                            <span>
                              <span
                                style={{
                                  color:
                                    u.latest_listing.sale_or_rental === "S"
                                      ? "#1976d2"
                                      : "#4caf50",
                                  fontWeight: 500,
                                  marginRight: 4,
                                }}
                              >
                                {u.latest_listing.sale_or_rental}
                              </span>
                              {formatPrice(u.latest_listing.list_price)}
                              <span
                                style={{
                                  color:
                                    STATUS_COLORS[
                                      u.latest_listing.listing_status
                                    ] || "#999",
                                  fontSize: 11,
                                  marginLeft: 4,
                                }}
                              >
                                {u.latest_listing.listing_status}
                              </span>
                              {u.latest_listing.created_date && (
                                <span style={{ fontSize: 10, color: "#999", marginLeft: 4 }}>
                                  {formatDate(u.latest_listing.created_date)}
                                </span>
                              )}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>

                      {/* Expanded history */}
                      {expandedUnit === u.unit_id && (
                        <tr key={`${u.unit_id}-history`}>
                          <td
                            colSpan={18}
                            style={{
                              background: "#f8fafd",
                              padding: "8px 16px 12px 16px",
                              borderBottom: "2px solid #e0e0e0",
                            }}
                          >
                            {historyLoading ? (
                              <span style={{ color: "#999", fontSize: 12 }}>
                                Loading history...
                              </span>
                            ) : unitHistory?.listings?.length ? (
                              <div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "#666",
                                    marginBottom: 6,
                                  }}
                                >
                                  LISTING HISTORY ({unitHistory.listings.length})
                                </div>
                                <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: 12,
                                    tableLayout: "fixed",
                                  }}
                                >
                                  <colgroup>
                                    <col style={{ width: 70 }} />
                                    <col style={{ width: 40 }} />
                                    <col style={{ width: 80 }} />
                                    <col style={{ width: 100 }} />
                                    <col style={{ width: 40 }} />
                                    <col style={{ width: 40 }} />
                                    <col style={{ width: 60 }} />
                                    <col style={{ width: 35 }} />
                                    <col style={{ width: 35 }} />
                                    <col style={{ width: 55 }} />
                                    <col style={{ width: 40 }} />
                                    <col style={{ width: 40 }} />
                                    <col style={{ width: 80 }} />
                                    <col style={{ width: 80 }} />
                                    <col style={{ width: 50 }} />
                                    <col style={{ width: 55 }} />
                                    <col style={{ width: 50 }} />
                                    <col style={{ width: 30 }} />
                                  </colgroup>
                                  <thead>
                                    <tr>
                                      {[
                                        "ID",
                                        "Type",
                                        "Price",
                                        "Status",
                                        "Beds",
                                        "Baths",
                                        "Sqft",
                                        "DW",
                                        "W/D",
                                        "Outdoor",
                                        "Reno",
                                        "Floor",
                                        "Listed",
                                        "Closed",
                                        "Verified",
                                        "Scraped",
                                        "SE Link",
                                        "",
                                      ].map((h) => (
                                        <th
                                          key={h}
                                          style={{
                                            ...th,
                                            fontSize: 11,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {h}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {unitHistory.listings.map((l) => (
                                      <tr
                                        key={l.listing_id}
                                        style={{
                                          borderBottom: "1px solid #eee",
                                        }}
                                      >
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                            fontSize: 11,
                                            color: "#888",
                                          }}
                                        >
                                          {l.listing_id}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                            fontWeight: 600,
                                            color:
                                              l.sale_or_rental === "S"
                                                ? "#1976d2"
                                                : "#4caf50",
                                          }}
                                        >
                                          {l.sale_or_rental}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {formatPrice(l.list_price)}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                            color:
                                              STATUS_COLORS[
                                                l.listing_status
                                              ] || "#666",
                                            fontSize: 11,
                                          }}
                                        >
                                          {l.listing_status}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {l.bedrooms ?? "-"}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {l.bathrooms ?? "-"}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {l.sqft
                                            ? `${l.sqft.toLocaleString()}${l.sqft_estimated ? "*" : ""}`
                                            : "-"}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {l.dishwasher === true
                                            ? "Y"
                                            : l.dishwasher === false
                                              ? "N"
                                              : "-"}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {l.washer_dryer || "-"}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {l.private_outdoor_space === true
                                            ? "Y"
                                            : l.private_outdoor_space === false
                                              ? "N"
                                              : "-"}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {l.renovation || "-"}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {l.floor ?? "-"}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {formatDate(l.created_date)}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {formatDate(l.closing_date)}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {l.verified ? (
                                            <span
                                              style={{
                                                color: "#4caf50",
                                                fontWeight: 600,
                                              }}
                                            >
                                              V
                                            </span>
                                          ) : (
                                            "-"
                                          )}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {l.se_scraped ? (
                                            <span
                                              style={{
                                                color: "#4caf50",
                                                fontWeight: 600,
                                              }}
                                            >
                                              Y
                                            </span>
                                          ) : (
                                            "-"
                                          )}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          {l.listing_url ? (
                                            <a
                                              href={l.listing_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                color: "#1976d2",
                                                fontSize: 11,
                                                textDecoration: "none",
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              SE
                                            </a>
                                          ) : (
                                            "-"
                                          )}
                                        </td>
                                        <td
                                          style={{
                                            ...td,
                                            padding: "4px 6px",
                                          }}
                                        >
                                          <button
                                            onClick={() =>
                                              setEditListing({
                                                id: l.listing_id,
                                                address: unitHistory?.unit?.address,
                                                unit_number: unitHistory?.unit?.unit_number,
                                              })
                                            }
                                            style={{
                                              background: "none",
                                              border: "none",
                                              cursor: "pointer",
                                              fontSize: 14,
                                              padding: "2px 4px",
                                            }}
                                            title="Edit listing"
                                          >
                                            &#9998;
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <span style={{ color: "#999", fontSize: 12 }}>
                                No listing history
                              </span>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                  {units.length === 0 && (
                    <tr>
                      <td
                        colSpan={18}
                        style={{ padding: 40, textAlign: "center", color: "#999" }}
                      >
                        No units found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 8,
                  marginTop: 16,
                  alignItems: "center",
                }}
              >
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  style={pageBtn}
                >
                  Prev
                </button>
                <span style={{ fontSize: 13, color: "#666" }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  style={pageBtn}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {saving && (
          <div
            style={{
              position: "fixed",
              bottom: 16,
              right: 16,
              background: "#333",
              color: "#fff",
              padding: "8px 16px",
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            Saving...
          </div>
        )}

        {editListing && (
          <ListingEditModal
            listing={editListing}
            onClose={() => setEditListing(null)}
            onSave={() => {
              setEditListing(null);
              if (expandedUnit) loadHistory(expandedUnit);
              loadData();
            }}
            onVerify={() => {
              setEditListing(null);
              if (expandedUnit) loadHistory(expandedUnit);
              loadData();
            }}
          />
        )}
      </div>
    </div>
  );
}

// ── Inline editable components ──

function EditableCheckbox({ value, onSave }) {
  const isNull = value === null || value === undefined;
  return (
    <span
      onClick={() => onSave(value === true ? false : true)}
      style={{
        cursor: "pointer",
        color: isNull ? "#aaa" : value ? "#4caf50" : "#555",
        fontWeight: value ? 600 : 400,
        userSelect: "none",
      }}
      title={isNull ? "Click to set" : `Click to toggle (${value ? "Yes" : "No"})`}
    >
      {isNull ? "?" : value ? "Y" : "N"}
    </span>
  );
}

function EditableSelect({ value, options, onSave }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onSave(e.target.value)}
      style={{
        border: "none",
        background: "transparent",
        fontSize: 13,
        cursor: "pointer",
        color: !value ? "#aaa" : "#333",
        padding: 0,
        width: "100%",
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function EditableNumber({ value, placeholder, onSave, width = 60 }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  if (editing) {
    return (
      <input
        type="number"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false);
          const v = draft ? parseInt(draft) : null;
          if (v !== value) onSave(v);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.target.blur();
          if (e.key === "Escape") setEditing(false);
        }}
        style={{
          width,
          border: "1px solid #1976d2",
          borderRadius: 3,
          padding: "2px 4px",
          fontSize: 13,
        }}
      />
    );
  }

  return (
    <span
      onClick={() => {
        setDraft(value != null ? String(value) : "");
        setEditing(true);
      }}
      style={{
        cursor: "pointer",
        color: value != null ? "#333" : "#aaa",
        borderBottom: "1px dashed #bbb",
        minWidth: 30,
        display: "inline-block",
      }}
      title="Click to edit"
    >
      {value != null ? value.toLocaleString() : placeholder || "-"}
    </span>
  );
}

// ── Styles ──

const th = {
  textAlign: "left",
  padding: "8px 8px",
  fontWeight: 600,
  fontSize: 12,
  color: "#666",
  whiteSpace: "nowrap",
};

const td = {
  padding: "6px 8px",
  whiteSpace: "nowrap",
};

const pageBtn = {
  padding: "6px 14px",
  borderRadius: 4,
  border: "1px solid #ccc",
  background: "#fff",
  cursor: "pointer",
  fontSize: 13,
};
